// StatTile.js — the clickable stat tile used across the Tier-2 row. Extracted
// from LocalIntelligencePage (pass 1): purely presentational, no page state.
import React from 'react';
import { MetricCardShell, TONE_STYLES } from '../shared/MetricCardShell';
import { fmtCount } from '../shared/format';

// The gray tone's value colour is CI's "no data" grey; a real count on a neutral tile
// reads as dark text instead, so 0 and 53 do not look switched off.
const valueClassFor = (tone) => (tone === 'gray' ? 'text-gray-900 dark:text-gray-100' : (TONE_STYLES[tone] || TONE_STYLES.gray).valueClass);

// Item 1 — `color` is the per-tile accent (a TONE_STYLES key). It defaults to
// `tone` so existing callers keep working, and drives the thin top border (via
// MetricCardShell's 3px border-t) and the big-number text color (via
// valueClassFor). The uppercase label (rendered by MetricCardShell) and the
// unit line stay in their current muted color — only the border and number take
// the accent. The background keeps MetricCardShell's subtle tone tint, never a
// fully colored card.
// Per-tile hover ring — the hover glow matches the tile's accent color (the
// `color` prop / TONE_STYLES key) instead of a uniform brand blue, so a green
// tile hovers green, a red tile hovers red, an amber tile amber, etc. The
// focus-visible ring stays brand-blue for one consistent keyboard-focus cue.
const TILE_HOVER_RING = {
  brandBlue: '[&>div]:hover:ring-[#32B1FF]/40',
  emerald:   '[&>div]:hover:ring-emerald-500/40',
  amber:     '[&>div]:hover:ring-amber-500/40',
  red:       '[&>div]:hover:ring-red-500/40',
  gray:      '[&>div]:hover:ring-gray-400/40 dark:[&>div]:hover:ring-gray-500/40',
  purple:    '[&>div]:hover:ring-purple-500/40',
};

const StatTile = ({ tone, color = tone, icon, title, tooltip, value, unit, onOpen }) => {
  const hoverRing = TILE_HOVER_RING[color] ?? TILE_HOVER_RING.brandBlue;
  return (
  <div
    role="button"
    tabIndex={0}
    onClick={onOpen}
    onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
    className={`h-full [&>div]:h-full [&>div]:hover:ring-2 ${hoverRing} cursor-pointer rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/50 hover:shadow-md transition-shadow`}
  >
    <MetricCardShell tone={color} icon={icon} title={title} tooltip={tooltip}>
      <div className="mt-2 flex items-end gap-2">
        <span className={`text-3xl font-extrabold leading-none ${valueClassFor(color)}`}>{fmtCount(value)}</span>
        {/* Chevron-right — the same glyph DrillOver and the search evidence tables
            use to mark a row as drillable. Sits at the value's baseline (ml-auto,
            mb-1) to signal the whole tile opens its underlying list. */}
        <svg
          className="w-4 h-4 mb-1 ml-auto text-gray-400 dark:text-gray-500"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M9 18l6-6-6-6" />
        </svg>
      </div>
      {/* Unit line — a small muted caption under the big number that says what the
          figure counts. Smaller and lighter than the number, following the app's
          existing muted-text hierarchy (text-xs gray-500/gray-400). */}
      {unit && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{unit}</div>
      )}
    </MetricCardShell>
  </div>
  );
};

export default StatTile;
